/**
 * Filesystem Registry Resolver — MCP-specific.
 * BET-99 / FEAT-182 / ARCH-19
 *
 * Reads source files from the project root and extracts collection slug sets
 * using the keyExtraction hints from the registry manifest.
 *
 * Design constraints:
 * - Dev-time only. Synchronous I/O is acceptable — this runs in orient/wrapup/review,
 *   not in response to user actions or page loads (BET-99 no-go #5).
 * - Heuristic parser, not AST-based (BET-99 no-go #8). Template literals with
 *   expression interpolation are not supported; backtick strings are treated as
 *   opaque (skipped to closing backtick). This is sufficient for the structured
 *   registries in the manifest.
 * - Gracefully skips unreadable files — never throws to callers.
 */

import { readFileSync } from "node:fs";
import { resolve } from "node:path";
import type { KeyExtraction, RegistryRef, ResolvedRegistries } from "./types.js";

/**
 * Resolve all registries from source files, returning slug sets keyed by registry ID.
 * Silently skips registries whose files can't be read or parsed.
 */
export function resolveRegistries(
  projectRoot: string,
  manifest: readonly RegistryRef[],
): ResolvedRegistries {
  if (!projectRoot) return {};

  const resolved: Record<string, ReadonlySet<string>> = {};

  for (const ref of manifest) {
    try {
      const filePath = resolve(projectRoot, ref.path);
      const content = readFileSync(filePath, "utf-8");
      const block = extractBlock(content, ref.exportName);
      if (!block) continue;

      const keys = extractKeys(block, ref.keyExtraction);
      if (keys.size > 0) {
        resolved[ref.id] = keys;
      }
    } catch (err) {
      if (process.env.DEBUG_COHERENCE) {
        console.debug(`[coherence] Skipping ${ref.id} (${ref.path}):`, err instanceof Error ? err.message : err);
      }
    }
  }

  return resolved;
}

// ── Block extraction ──────────────────────────────────────────────────────────

function extractBlock(content: string, exportName: string): string | null {
  const escaped = exportName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(
    `(?:export\\s+)?(?:const|let|var)\\s+${escaped}\\b[^=]*=\\s*`,
  );
  const match = pattern.exec(content);
  if (!match) return null;

  let pos = match.index + match[0].length;
  while (pos < content.length && /\s/.test(content[pos]!)) pos++;

  const ch = content[pos];

  if (ch === "{" || ch === "[") {
    return extractBalanced(content, pos, ch, ch === "{" ? "}" : "]");
  }

  // new Set([...])
  if (content.slice(pos).startsWith("new Set") || content.slice(pos).startsWith("new\nSet") || content.slice(pos).startsWith("new\n\tSet")) {
    const setMatch = content.slice(pos).match(/^new\s+Set\s*\(\s*\[/);
    if (setMatch) {
      return extractBalanced(content, pos + setMatch[0].length - 1, "[", "]");
    }
  }

  return null;
}

function extractBalanced(
  content: string,
  openPos: number,
  openChar: string,
  closeChar: string,
): string | null {
  let depth = 1;
  let i = openPos + 1;

  while (i < content.length && depth > 0) {
    const ch = content[i]!;

    if (ch === "'" || ch === '"' || ch === "`") {
      i = skipString(content, i, ch);
      continue;
    }
    if (ch === "/" && i + 1 < content.length && content[i + 1] === "/") {
      while (i < content.length && content[i] !== "\n") i++;
      continue;
    }
    if (ch === "/" && i + 1 < content.length && content[i + 1] === "*") {
      i += 2;
      while (i < content.length) {
        if (content[i] === "/" && i > 0 && content[i - 1] === "*") { i++; break; }
        i++;
      }
      continue;
    }

    if (ch === openChar) depth++;
    else if (ch === closeChar) depth--;
    i++;
  }

  if (depth !== 0) return null;
  return content.slice(openPos + 1, i - 1);
}

/**
 * Skip past a string literal. Handles single/double quotes with backslash escapes.
 * Template literals: skips to closing backtick but does NOT handle ${} interpolation —
 * nested braces inside template expressions may cause early termination. Acceptable
 * because registry files use simple string literals, not template expressions.
 */
function skipString(content: string, start: number, quote: string): number {
  let i = start + 1;
  while (i < content.length) {
    const ch = content[i]!;
    if (ch === "\\") {
      i += 2;
      continue;
    }
    if (ch === quote) return i + 1;
    i++;
  }
  return i;
}

// ── Key extraction ────────────────────────────────────────────────────────────

function extractKeys(block: string, extraction: KeyExtraction): Set<string> {
  switch (extraction) {
    case "object-keys":
      return extractObjectKeys(block);
    case "array-slug":
      return extractArraySlugs(block);
    case "set-values":
      return extractSetValues(block);
  }
}

/**
 * Extract top-level keys from an object literal block.
 * Tracks brace/bracket depth so nested property names are ignored.
 */
function extractObjectKeys(block: string): Set<string> {
  const keys = new Set<string>();
  let depth = 0;
  let i = 0;

  while (i < block.length) {
    const ch = block[i]!;

    if (ch === "/" && i + 1 < block.length && block[i + 1] === "/") {
      while (i < block.length && block[i] !== "\n") i++;
      continue;
    }
    if (ch === "/" && i + 1 < block.length && block[i + 1] === "*") {
      i += 2;
      while (i < block.length) {
        if (block[i] === "/" && i > 0 && block[i - 1] === "*") { i++; break; }
        i++;
      }
      continue;
    }

    // At depth 0, try matching quoted keys before consuming strings as values
    if (depth === 0) {
      const rest = block.slice(i);
      const quoted = rest.match(/^(['"])([\w-]+)\1\s*:/);
      if (quoted) {
        keys.add(quoted[2]!);
        i += quoted[0].length;
        continue;
      }
      const ident = rest.match(/^([\w-]+)\s*:/);
      if (ident) {
        keys.add(ident[1]!);
        i += ident[0].length;
        continue;
      }
    }

    if (ch === "'" || ch === '"' || ch === "`") {
      i = skipString(block, i, ch);
      continue;
    }

    if (ch === "{" || ch === "[" || ch === "(") { depth++; i++; continue; }
    if (ch === "}" || ch === "]" || ch === ")") { depth--; i++; continue; }

    i++;
  }

  return keys;
}

function extractArraySlugs(block: string): Set<string> {
  const slugs = new Set<string>();
  for (const m of block.matchAll(/slug:\s*['"](\w[\w-]*)['"]/g)) {
    slugs.add(m[1]!);
  }
  return slugs;
}

function extractSetValues(block: string): Set<string> {
  const values = new Set<string>();
  for (const m of block.matchAll(/['"](\w[\w-]*)['"]/g)) {
    values.add(m[1]!);
  }
  return values;
}
